import { createFormControl } from 'react-hook-form';
import { CollateralFormDataValues } from './schema';
import { useCallback, useMemo } from 'react';
import { useCollateralFormStore } from './store';
import { real_estate_hooks } from '@axo/shared/data-access/hooks';

export const useCollateralFormOnSubmitFactory = ({
  applicationID,
  formContext,
}: {
  applicationID: string;
  formContext: ReturnType<typeof createFormControl<CollateralFormDataValues>>;
}) => {
  const {
    isEditing,
    localOnly,
    actions: { stopEditingAll },
  } = useCollateralFormStore(applicationID);

  const { mutateAsync: createEstate } = real_estate_hooks.useCreateRealEstate();
  const { mutateAsync: patchEstate } = real_estate_hooks.usePatchRealEstate();
  const { mutateAsync: addApplicationCollateral } =
    real_estate_hooks.useAddApplicationCollateral();
  const { mutateAsync: removeApplicationCollateral } =
    real_estate_hooks.useRemoveApplicationCollateral();

  const onSubmit = useCallback(async () => {
    try {
      const values = structuredClone(formContext.getValues());

      const createEstatesJobs = Array.from(localOnly).map(async (id) => {
        const estateFormData = values.estates[id];

        if (!estateFormData)
          throw new Error("Couldn't find localOnly estate with id: " + id);

        const estate = await createEstate({
          ...estateFormData,
          Source: 'ace',
        });

        if (values.isCollateral[id])
          await addApplicationCollateral({
            applicationID,
            estateID: estate.ID,
          });
      });

      const patchEstatesJobs = Array.from(isEditing)
        .filter((id) => !localOnly.has(id))
        .map(async (id) => {
          const estateFormData = values.estates[id];

          if (!estateFormData)
            throw new Error(
              "Couldn't find non-localOnly estate with id: " + id,
            );

          await patchEstate({
            id,
            patch: { ...estateFormData, Source: 'ace' },
          });
        });

      const patchApplicationCollateralsJobs = Array.from(isEditing)
        .filter(
          (id) =>
            !localOnly.has(id) &&
            formContext.getFieldState(`isCollateral.${id}`).isDirty,
        )
        .map(async (id) => {
          if (values.isCollateral[id]) {
            await addApplicationCollateral({ applicationID, estateID: id });
          } else if (values.estates[id].CollateralID) {
            await removeApplicationCollateral({
              applicationID,
              collateralID: values.estates[id].CollateralID,
            });
          }
        });

      await Promise.all([
        ...createEstatesJobs,
        ...patchEstatesJobs,
        ...patchApplicationCollateralsJobs,
      ]);
    } finally {
      stopEditingAll();
      formContext.reset();
    }
  }, [
    formContext,
    stopEditingAll,
    createEstate,
    localOnly,
    patchEstate,
    isEditing,
    addApplicationCollateral,
    removeApplicationCollateral,
    applicationID,
  ]);

  return useMemo(
    () => formContext.handleSubmit(onSubmit, onSubmit),
    [formContext, onSubmit],
  );
};
